import { useEffect, useState } from 'react';
import { apiUrl } from '../../../api/apiUrl';
import http, { authHeaders } from '../../../api/axios';
import DataTable from '../../../components/table/DataTable';
import { toast } from 'react-toastify';
import { columns } from '../../../components/columns/Company/SmartAttachmentsColumns';
import { Loader } from '../../../components/loader/Loader';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { SmartAttachment } from '../../../types/SmartAttachment';
import { PlusIcon } from '@heroicons/react/24/outline';
import Button from '../../../components/button/Button';
import { paths } from '../../RouterContainer';
import { useTranslation } from '../../../context/translationContext';
import { downloadFile } from '../../../utils/functions/file.functions';
import { Modal } from '../../../components/modal/modal';
import PdfPreview from '../../../components/filePreview/FilePreview';
import { useLoading } from '../../../context/loadingContext';

/**
 * Page component represents list attachments
 */
export const SmartAttachmentPage = () => {
  const { id: invoiceId } = useParams();
  const [attachments, setAttachments] = useState<SmartAttachment[] | null>(null);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [selectedId, setSelectedId] = useState<string>();
  const [previewOpen, setPreviewOpen] = useState<boolean>(false);
  const { startLoading, stopLoading } = useLoading();
  /**
   * Loads smart attachments for current document
   */
  const fetchAttchments = async () => {
    const req = `?parentId=${invoiceId}`;
    const response = await http.apiGet({
      url: `${apiUrl.SMART_ATTACHMENT_LIST}${req}`,
    });
    setAttachments(response?.data);
  };

  useEffect(() => {
    if (!invoiceId) return;
    fetchAttchments();
  }, []);

  /**
   * Redirects to smart attachment edit page
   * @param id
   */
  const onEditItem = (id: string | number) => {
    if (!id) return;
    navigate(`/${paths.INVOICE}/${invoiceId}/${paths.SMART_ATTACHMENTS}/edit/${id}`);
  };

  /**
   * Removes file
   * @param id of entity
   */
  const onRemoveItem = async (id: string | number) => {
    if (id === null) return;
    const response = await http.apiDelete({
      url: `${apiUrl.SMART_ATTACHMENT_DELETE}?id=${id}`,
    });
    if (response?.status === 204) {
      toast.success('Success!');
      fetchAttchments();
    }
  };

  /**
   * Opens preview of pdf
   */
  const handleOpenPreview = async (id: number) => {
    startLoading();
    await http
      .apiGet({
        url: `${apiUrl.SMART_ATTACHMENT_GET_PDF}?id=${id}`,
      })
      .then(async (res) => {
        if (!res) return;
        setSelectedId(res.data);
        stopLoading();
      });
    stopLoading();

    setPreviewOpen(true);
  };

  /**
   * donwload pdf by id
   */
  const handleDownloadPdf = async (id: number) => {
    startLoading();
    await http
      .apiGet({
        url: `${apiUrl.SMART_ATTACHMENT_GET_PDF}?id=${id}`,
      })
      .then(async (res) => {
        if (!res) return;
        await http
          .apiGetFiles({
            url: `${apiUrl.FILE_DOWNLOAD}?id=${res.data}`,
            config: {
              headers: {
                ...authHeaders(),
                'Content-Type': 'application/json',
              },
              method: 'GET',
              responseType: 'blob',
            },
          })
          .then(async (response) => {
            // get filename
            const filename = await response?.headers['content-disposition']
              ?.split('filename=')[1]
              .split(';')[0];
            const fileUrl: string = URL.createObjectURL(response?.data);
            downloadFile(fileUrl, filename ? filename : 'smart-attachment');
            stopLoading();
          });
      });
    stopLoading();
  };

  if (attachments === null) return <Loader />;
  return (
    <>
      <div className="flex justify-end">
        <Link to={`/${paths.INVOICE}/${invoiceId}/${paths.SMART_ATTACHMENTS}/create`}>
          <Button
            title={t('general.create-new')}
            color="green"
            icon={<PlusIcon className="w-4 h-4 mr-2" />}
          />
        </Link>
      </div>
      <DataTable
        data={attachments}
        columns={columns}
        handleRemove={onRemoveItem}
        handleEdit={onEditItem}
        customButtons={[
          {
            title: t('general.download'),
            color: 'blue',
            onSubmit: (e) => handleDownloadPdf(e as number),
          },
          {
            title: t('general.preview'),
            color: 'blue',
            onSubmit: (e) => handleOpenPreview(e as number),
          },
        ]}
      />
      {selectedId && (
        <Modal
          open={previewOpen}
          title={
            attachments?.find((f: SmartAttachment) => f.id === selectedId)?.header ?? 'Preview'
          }
          className={`min-w-[100%] !m-0 !rounded-none`}
          onClose={() => setPreviewOpen(false)}
        >
          <PdfPreview id={selectedId} />
        </Modal>
      )}
    </>
  );
};
